import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Metadata, RunView } from '@memberjunction/core';
import { ConnectorEntity, ContactEntity, EntityTrainingDataEntity, InstanceContactEntity, InstanceEntity, InstanceEntityExtended, OrganizationContactEntity, OrganizationEntity, QueryTrainingDataEntity } from 'mj_generatedentities';
import { UserContextService } from 'src/app/core/services/user-context.service';
import { SharedService } from 'src/app/core/services/shared-service';
import { AddEvent, RemoveEvent } from '@progress/kendo-angular-grid';
import { buildRoutePrefix } from 'src/app/core/util';
import { InstanceProviderService } from 'src/app/dashboard/instance-provider.service';

  
@Component({
  selector: 'app-settings-internal-admin',
  templateUrl: './internal-admin.component.html',
  styleUrls: ['./internal-admin.component.css']
})
export class InternalAdminSettingsComponent implements OnInit{
  _md: Metadata;
 
  public AllConnectors: ConnectorEntity[] = [];
  public AllOrganizations: OrganizationEntity[] = [];
  public AllInstances: InstanceEntity[] = [];
  public AllContacts: ContactEntity[] = [];
  public AllEntityTrainingData: EntityTrainingDataEntity[] = [];
  public AllQueryTrainingData: QueryTrainingDataEntity[] = [];

  public editQueryTrainingDataItem: QueryTrainingDataEntity;
  public editQueryTrainingDataItem_Connector: ConnectorEntity;
  public editEntityTrainingDataItem: EntityTrainingDataEntity;
  public isQueryTrainingDataNew: boolean = false;
  public isEntityTrainingDataNew: boolean = false;

  public showQueryEditor: boolean = false;
  public showEntityEditor: boolean = false;
  public showInstance: boolean = false;
  public showWarning: boolean = false;
  
  public actionType: '' | 'lock' | 'archive' = '';

  public AllEntityTrainingDataTypes: string[] = ['Entity', 'Field'];
  public editEntityTrainingDataItem_Connector: ConnectorEntity;
  public editEntityTrainingDataItem_Type: 'Entity' | 'Field';

  public AllEntityValuesToPackOptions: string[] = ['Auto','All','None'];
  public AllEntityRowsToPackSampleMethodOptions: string[] = ['random','top n','bottom n'];
  public AllEntityScopeDefaultOptions: string[] = ['All','Users','Admins','AI'];
  public AllEntityRowsToPackWithSchemaOptions: string[] = ['None','All','Sample'];

  public editEntityScopeDefaultOptions: string[] = [];

  constructor(
    private userContextService: UserContextService,
    private instanceProvider: InstanceProviderService,
    private sharedService: SharedService,
    private router: Router,
  ) {
    this._md = new Metadata();
  }

  async ngOnInit(): Promise<void> {
    const rv = new RunView();
    const pOrgs = rv.RunView<OrganizationEntity>({
      EntityName: 'Organizations',
      OrderBy: 'Name',
      ResultType: 'entity_object'
    });
    const pContacts = rv.RunView<ContactEntity>({
      EntityName: 'Contacts',
      OrderBy: 'LastName, FirstName',
      ResultType: 'entity_object'
    });
    const pInstances = rv.RunView<InstanceEntity>({
      EntityName: 'Instances',
      OrderBy: 'Name',
      ResultType: 'entity_object'
    });
    const pConnectors = rv.RunView<ConnectorEntity>({
      EntityName: 'Connectors',
      OrderBy: 'Name',
      ResultType: 'entity_object'
    });
    const pEntityTrainingData = rv.RunView<EntityTrainingDataEntity>({
      EntityName: 'Entity Training Datas',
      OrderBy: 'Connector, EntityName, FieldName',
      ResultType: 'entity_object'
    });
    const pQueryTrainingData = rv.RunView<QueryTrainingDataEntity>({
      EntityName: 'Query Training Datas',
      OrderBy: 'Connector, Name',
      ResultType: 'entity_object'
    });

    const [orgs, contacts, instances, connectors, etd, qtd] = await Promise.all([pOrgs, pContacts, pInstances, pConnectors, pEntityTrainingData, pQueryTrainingData]);
    this.AllOrganizations = orgs.Results;
    this.AllContacts = contacts.Results;
    this.AllInstances = instances.Results;
    this.AllConnectors = connectors.Results;  
    this.AllEntityTrainingData = etd.Results;
    this.AllQueryTrainingData = qtd.Results;    
  }
 

  public async addQueryHandler() {
    const newQtd = await this._md.GetEntityObject<QueryTrainingDataEntity>('Query Training Datas');
    this.editQueryTrainingDataItem = newQtd;
    this.isQueryTrainingDataNew = true;
    this.editQueryTrainingDataItem_Connector = this.AllConnectors[0];
    this.showQueryEditor = true;
  }

  public editQueryHandler(args: AddEvent): void {
    this.editQueryTrainingDataItem = args.dataItem;
    this.isQueryTrainingDataNew = false;
    this.editQueryTrainingDataItem_Connector = this.AllConnectors.find(x => x.ID === this.editQueryTrainingDataItem.ConnectorID);
    this.showQueryEditor = true;
  }

  public closeQueryEditor(): void {
    this.showQueryEditor = false;
    this.editQueryTrainingDataItem = undefined;
  }

  public async saveQueryHandler() {
    const item = this.editQueryTrainingDataItem;
    if (!item )
      return;

    item.ConnectorID = this.editQueryTrainingDataItem_Connector.ID;
    if (!await item.Save()) {
      this.sharedService.DisplayNotification('Error saving Query Training Data', 'error');
    }
    else {
      this.editQueryTrainingDataItem = undefined;
      this.showQueryEditor = false;

      // check to see if item is in the array, if not add it
      const idx = this.AllQueryTrainingData.findIndex(x => x.ID === item.ID);
      if (idx < 0)
        this.AllQueryTrainingData.push(item);
    }
  }

  public async removeQueryHandler(args: RemoveEvent) {
    const item = args.dataItem as QueryTrainingDataEntity;
    if (!await item.Delete()) {
      this.sharedService.DisplayNotification('Error deleting Query Training Data', 'error');
    }
    else {
      this.sharedService.DisplayNotification('Query Training Data deleted', 'success');
      // remove the item from the array now
      const idx = this.AllQueryTrainingData.findIndex(x => x.ID === item.ID);
      if (idx >= 0)
        this.AllQueryTrainingData.splice(idx, 1);
    }
  }

  /////////////////////////// ENTITY STUFF
  public async addEntityHandler() {
    const newQtd = await this._md.GetEntityObject<EntityTrainingDataEntity>('Entity Training Datas');
    this.editEntityTrainingDataItem = newQtd;
    this.editEntityScopeDefaultOptions = [];
    this.isEntityTrainingDataNew = true;
    this.editEntityTrainingDataItem_Connector = this.AllConnectors[0];
    this.showEntityEditor = true;
  }

  public editEntityHandler(args: AddEvent): void {
    this.editEntityTrainingDataItem = args.dataItem;
    this.editEntityScopeDefaultOptions = this.editEntityTrainingDataItem.ScopeDefault ? this.editEntityTrainingDataItem.ScopeDefault.split(',') : [];
    this.isEntityTrainingDataNew = false;
    this.editEntityTrainingDataItem_Connector = this.AllConnectors.find(x => x.ID === this.editEntityTrainingDataItem.ConnectorID);
    this.showEntityEditor = true;
  }

  public closeEntityEditor(): void {
    this.showEntityEditor = false;
    this.editEntityTrainingDataItem = undefined;
  }

  public async saveEntityHandler() {
    const item = this.editEntityTrainingDataItem;
    if (!item )
      return;

    item.ScopeDefault = this.editEntityScopeDefaultOptions.join(',');
    item.ConnectorID = this.editEntityTrainingDataItem_Connector.ID;
    if (item.RowsToPackSampleCount && typeof item.RowsToPackSampleCount !== 'number') {
      const numValue = Number(item.RowsToPackSampleCount);
      if (!isNaN(numValue)) {
        item.RowsToPackSampleCount = numValue;
      }      
    }

    if (!await item.Save()) {
      this.sharedService.DisplayNotification('Error saving Entity Training Data', 'error');
    }
    else {
      this.editEntityTrainingDataItem = undefined;
      this.showEntityEditor = false;

      // check to see if item is in the array, if not add it
      const idx = this.AllEntityTrainingData.findIndex(x => x.ID === item.ID);
      if (idx < 0)
        this.AllEntityTrainingData.push(item);
    }
  }

  public async removeEntityHandler(args: RemoveEvent) {
    const item = args.dataItem as EntityTrainingDataEntity;
    if (!await item.Delete()) {
      this.sharedService.DisplayNotification('Error deleting Entity Training Data', 'error');
    }
    else {
      this.sharedService.DisplayNotification('Entity Training Data deleted', 'success');
      // remove the item from the array now
      const idx = this.AllEntityTrainingData.findIndex(x => x.ID === item.ID);
      if (idx >= 0)
        this.AllEntityTrainingData.splice(idx, 1);
    }
  }


  public handleEntityDoubleClick(event: MouseEvent) {
    // get the currently selected row from the entity grid
    if (this.selectedEntityTrainingData) {
      this.editEntityHandler({ dataItem: this.selectedEntityTrainingData } as AddEvent);
    }
  }
  private selectedEntityTrainingData: EntityTrainingDataEntity;
  public handleEntitySelectionChange(event: any ) {
    this.selectedEntityTrainingData = event.selectedRows[0]?.dataItem;
  }


  public handleQueryDoubleClick(event: MouseEvent) {
    // get the currently selected row from the entity grid
    if (this.selectedQueryTrainingData) {
      this.editQueryHandler({ dataItem: this.selectedQueryTrainingData } as AddEvent);
    }
  }
  private selectedQueryTrainingData: QueryTrainingDataEntity;
  public handleQuerySelectionChange(event: any ) {
    this.selectedQueryTrainingData = event.selectedRows[0]?.dataItem;
  }

  private selectedInstance: InstanceEntityExtended;
  public async handleInstanceClick(event: any) {
    console.log("Instance Clicked");
    const item = event.dataItem as InstanceEntityExtended;
    this.selectedInstance = item;
    if (this.selectedInstance) {
      this.showInstance = true;
    }
  }

  public confirmAction(action: 'lock' | 'archive'): void {
    this.actionType = action;
    this.showWarning = true;
  }
  
  public closeWarning(): void {
    this.showWarning = false;
  }

  public async executeAction(): Promise<void> {
    this.closeWarning();
    if (this.actionType === 'lock') {
      await this.lockInstance();
    } else if (this.actionType === 'archive') {
      await this.archiveInstance();
    }
  }

  public closeInstance() {
    this.showInstance = false;
    this.selectedInstance = null;
  }

  public async inspectInstance() {
    if (!this.selectedInstance) {
      return;
    }

    const instanceContact = this.userContextService.InstanceContacts.find(x => x.InstanceID === this.selectedInstance.ID);
    if (instanceContact) {
      this.userContextService.SetCurrentInstance(instanceContact.InstanceID);
    } else {
      try {
        this.sharedService.markGlobalLoading(true);
        // Create Instance contact, the backend will validate that the contact is a super admin contact
        const md = new Metadata();
        
        const pSystemAdminRole = this.userContextService.SystemAdminRole;
        const pOrgContact = md.GetEntityObject<OrganizationContactEntity>('Organization Contacts');
        const pOnstContact = md.GetEntityObject<InstanceContactEntity>('Instance Contacts');

        const [systemAdminRole, orgContact, instContact] = await Promise.all([pSystemAdminRole, pOrgContact, pOnstContact]);

        orgContact.NewRecord();
        orgContact.OrganizationID = this.selectedInstance.OrganizationID;
        orgContact.ContactID = this.userContextService.Contact.ID;
        orgContact.ContactRoleID = systemAdminRole.ID;
        orgContact.Active = true;

        if(!await orgContact.Save()) {
          this.sharedService.DisplayNotification('Error saving Organization Contact', 'error');
        }

        instContact.NewRecord();
        instContact.InstanceID = this.selectedInstance.ID;
        instContact.ContactID = this.userContextService.Contact.ID;
        instContact.IsActive = true;

        if(!await instContact.Save()) {
          this.sharedService.DisplayNotification('Error saving Instance Contact', 'error');
        }

        this.userContextService.SetCurrentInstanceAsSuperAdmin(this.selectedInstance);
        const prefixURl = buildRoutePrefix(this.userContextService.CurrentInstance.ID);
        this.router.navigate([prefixURl, 'chat']);
    
        this.sharedService.markGlobalLoading(false);
      } catch (error) {
        console.error(error);
        this.sharedService.DisplayNotification('Error inspecting Instance', 'error');
      } finally {
        this.sharedService.markGlobalLoading(false);
      }
    }

    await this.instanceProvider.TriggerInstanceProviderSetup(this.selectedInstance.ID);
    this.closeInstance();
  }

  public async lockInstance() {
    this.selectedInstance.Status = 'Locked';
    if (!await this.selectedInstance.Save()) {
      this.sharedService.DisplayNotification('Error saving Instance', 'error');
    }

    this.closeInstance();
  }

  public async unlockInstance() {
    this.selectedInstance.Status = 'Active';
    if (!await this.selectedInstance.Save()) {
      this.sharedService.DisplayNotification('Error saving Instance', 'error');
    }

    this.closeInstance();
  }

  public async archiveInstance() {
    this.selectedInstance.Status = 'Archived'; // TODO: uncomnment this line
    if (!await this.selectedInstance.Save()) {
      this.sharedService.DisplayNotification('Error saving Instance', 'error');
    }
    
    this.closeInstance();
  }
}
